import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getTreatmentValue,
  type TreatmentValueSummary,
  getTreatmentValueOverview,
} from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  formatCurrency,
  formatPercent,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

export type UseTreatmentValueOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type TreatmentValueActivityRow = {
  period: string
  billed: string
  collected: string
  potential: string
  collectedPct: string
}

export type TreatmentValueChartRow = {
  label: string
  value: number
}

export type UseTreatmentValueResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  summary: TreatmentValueSummary | undefined
  billedStr: string
  collectedStr: string
  potentialStr: string
  chart: {
    data: TreatmentValueChartRow[]
    yDomain: [number, number]
    yTicks: number[]
  }
  activityRows: TreatmentValueActivityRow[]
}

function useTreatmentValueFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseTreatmentValueOptions & {
  fetcher: DashboardPeriodFetcher<Awaited<ReturnType<typeof getTreatmentValue>>>
}): UseTreatmentValueResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const summary = data?.summary

  const billedStr = useMemo(
    () => formatCurrency(summary?.billed.current ?? 0, { compact: true }),
    [summary],
  )

  const collectedStr = useMemo(
    () => formatCurrency(summary?.collected.current ?? 0, { compact: true }),
    [summary],
  )

  const potentialStr = useMemo(
    () => formatCurrency(summary?.potential.current ?? 0, { compact: true }),
    [summary],
  )

  const chart = useMemo(() => {
    const billed = summary?.billed.current ?? 0
    const collected = summary?.collected.current ?? 0
    const potential = summary?.potential.current ?? 0

    const chartData: TreatmentValueChartRow[] = [
      { label: 'Billed', value: billed / 1000 },
      { label: 'Collected', value: collected / 1000 },
      { label: 'Potential', value: potential / 1000 },
    ]

    const axis = computeChartAxis(chartData.map((d) => d.value))

    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
    }
  }, [summary])

  const activityRows = useMemo((): TreatmentValueActivityRow[] => {
    return (data?.byPeriod ?? []).map((row) => ({
      period: row.period,
      billed: formatCurrency(row.billed, { compact: true }),
      collected: formatCurrency(row.collected, { compact: true }),
      potential: formatCurrency(row.potential, { compact: true }),
      collectedPct: formatPercent(
        row.billed === 0 ? 0 : (row.collected / row.billed) * 100,
      ),
    }))
  }, [data])

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      summary?.billed.current,
      summary?.collected.current,
      summary?.potential.current,
    ),
    summary,
    billedStr,
    collectedStr,
    potentialStr,
    chart,
    activityRows,
  }
}

export function useTreatmentValue(
  options: UseTreatmentValueOptions,
): UseTreatmentValueResult {
  return useTreatmentValueFromFetcher({
    ...options,
    fetcher: getTreatmentValue,
  })
}

export function useTreatmentValueOverview(
  options: UseTreatmentValueOptions,
): UseTreatmentValueResult {
  return useTreatmentValueFromFetcher({
    ...options,
    fetcher: getTreatmentValueOverview,
  })
}
